import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { eventsApi, guestApi, formatDate } from "../lib/api";
import { Camera, PartyPopper, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function GuestEntry() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [nickname, setNickname] = useState("");
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadEvent();
  }, [eventId]);

  const loadEvent = async () => {
    try {
      const { data } = await eventsApi.get(eventId);
      setEvent(data);
    } catch {
      setError("Evento nao encontrado");
    } finally {
      setLoading(false);
    }
  };

  const getDeviceId = () => {
    let id = localStorage.getItem("festa_device_id");
    if (!id) {
      id = crypto.randomUUID?.() || Math.random().toString(36).slice(2);
      localStorage.setItem("festa_device_id", id);
    }
    return id;
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!nickname.trim()) return;
    setJoining(true);
    try {
      const deviceId = getDeviceId();
      const { data } = await guestApi.join(eventId, { nickname: nickname.trim(), device_id: deviceId });
      localStorage.setItem(`festa_guest_${eventId}`, JSON.stringify(data.guest));
      navigate(`/feed/${eventId}`);
    } catch (err) {
      setError("Erro ao entrar no evento");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF7] flex flex-col" data-testid="guest-entry-page">
      {/* Header */}
      <header className="w-full bg-[#FFFBF7]/80 backdrop-blur-xl border-b-2 border-[#1C1917]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FF6B6B] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shadow-[2px_2px_0px_#1C1917]">
            <Camera className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-black text-lg tracking-tight text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Festa.IA</span>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          {error && !event ? (
            <div className="bg-white border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl p-8 text-center">
              <p className="text-[#1C1917] font-bold">{error}</p>
              <Button
                onClick={() => navigate("/")}
                className="mt-4 bg-[#FF6B6B] text-white border-2 border-[#1C1917] rounded-full font-bold shadow-[4px_4px_0px_#1C1917]"
              >
                Voltar ao Inicio
              </Button>
            </div>
          ) : (
            <div className="bg-white border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl p-6 sm:p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#FEE2E2] border-2 border-[#1C1917] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-[2px_2px_0px_#1C1917]">
                  <PartyPopper className="w-8 h-8 text-[#FF6B6B]" strokeWidth={2.5} />
                </div>
                <h1 className="text-2xl font-black text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {event?.name}
                </h1>
                <p className="text-sm text-[#78716C] mt-1">{formatDate(event?.date)}</p>
              </div>

              {error && (
                <div className="bg-[#FEE2E2] border-2 border-[#1C1917] rounded-lg p-3 mb-4 text-sm text-[#1C1917] font-medium" data-testid="join-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleJoin} className="space-y-4">
                <div>
                  <Label className="text-sm font-bold text-[#1C1917]">Seu Apelido</Label>
                  <Input
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="Como voce quer ser chamado?"
                    required
                    className="mt-1 border-2 border-[#1C1917] rounded-lg"
                    data-testid="nickname-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={joining}
                  className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all active:translate-y-[4px] active:shadow-none"
                  data-testid="join-event-submit-btn"
                >
                  {joining ? <Loader2 className="w-5 h-5 animate-spin" /> : "Entrar na Festa!"}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
